import React, { ChangeEvent, InputHTMLAttributes, useState } from 'react'
import { DateRangeInput } from '@datepicker-react/styled'
import cn from 'classnames'
import styled, { css, ThemeProvider } from 'styled-components'

import { $secondaryWithAlpha, colors } from '../styleGuide'
import Datepicker from './components/Datepicker'
import Input from './components/Input'
import Inputmask from './components/Inputmask'
import TypeAHead from './components/TypeAHead'

interface DateRangeData {
  startDate: Date | null
  endDate: Date | null
  focusedInput?: boolean
}

interface OnDatesChangeProps {
  startDate: Date | null
  endDate: Date | null
  focusedInput: 'startDate' | 'endDate' | null
}

type CustomTheme = {
  breakpoints: string[]
  reactDatepicker: {
    dateRangeZIndex: number
  }
  splash: {
    bg: string
    gradient: string
    logoFill: string
    progressBg: string
    progressAfterBg: string
    titleColor: string
    subTitleColor: string
    iconColor: string
  }
  layout: {
    bg: string
    contentBoxShadowColor: string
  }
  search: {
    iconColor: string
  }
  sidebar: {
    bg: string
    boxShadow: string
  }
  options: {
    bg: string
    boxShadow: string
  }
  unselectedChat: {
    bg: string
    boxShadow: string
  }
  chatRoom: {
    bg: string
    boxShadow: string
  }
  common: {
    bg: string
    boxShadow: string
  }
  alert: {
    bg: string
    boxShadow: string
  }
  badge: {
    bg: string
    boxShadow: string
  }
  sentMessage: {
    bg: string
    boxShadow: string
  }
  receivedMessage: {
    bg: string
    boxShadow: string
  }
  encryptionMessage: {
    bg: string
    boxShadow: string
  }
}

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  _className?: string
  _label?: string
  _children?: React.ReactNode
  _isTextarea?: boolean
  _startDate?: Date | null
  _endDate?: Date | null
  name: string
  error?: string
  variant?: 'normal' | 'solid' | 'outline'
  dimension?: 'big' | 'medium' | 'small'
  shadow?: boolean
  disabled?: boolean
  as?: 'text' | 'textarea' | 'datePicker' | 'dateRange' | 'typeahead'
  type?: string
  onChange?: (_e: ChangeEvent<HTMLInputElement>) => void
  value?: string | DateRangeData
  inputClassName?: string
  isMakingInput?: boolean
  parentStyle?: React.CSSProperties
  labelKey?: string
  noPadding?: boolean
  invalid?: boolean
  showsearchicon?: number
  placeholder?: string
  isShowLabel?: boolean
  customLabel?: string
  isClearOnSelection?: boolean
  onSelectedOption?: (_option: any) => void
  showResetDate?: boolean
  showClose?: boolean
}

const variantClasses = {
  normal: 'bg-gray-100 border border-border-base rounded focus:shadow focus:bg-light focus:border-accent',
  solid: 'bg-gray-100 border border-border-100 rounded focus:bg-light focus:border-accent',
  outline: 'border border-border-base rounded focus:border-accent',
  line: 'ps-0 border-b border-border-base rounded-none focus:border-accent',
}

const StyledLabel = styled.div<{ focus?: string; showsearchicon: number }>`
  &.showLabell {
    background: ${(props) =>
      props.focus === 'true'
        ? 'linear-gradient(180deg, #fff 70%, transparent 30%);'
        : props.focus === 'false' && 'linear-gradient(180deg, #fff 52%, transparent 48%);'};
    top: ${(props) => (props.focus === 'true' ? '-10px' : props.focus === 'false' && '-8px')};
    opacity: 1;
    z-index: 1;
  }
  font-weight: 400;
  opacity: 0;
  color: ${colors.grayMedium};
  position: absolute;
  width: auto;
  top: -10px;
  left: ${(props) => (props.showsearchicon === 0 ? '27px' : '13px')};
  font-size: 11px;
  transition: all 0.2s ease-in-out;
  border-radius: 4px !important;
`

const PixelInputContainer = styled.div<Props>`
  position: relative;
  width: 100%;
  &.overFlowCustom {
    overflow: visible !important;
    & > * {
      overflow: visible !important;
      & > * {
        input {
          ${(props) =>
            props.invalid === true &&
            css`
              color: red !important;
            `}
        }
      }
    }
  }
  & > * {
    overflow: visible !important;
  }
  textarea {
    resize: none;
    height: 150px;
    min-width: 100%;
    outline: none;
    &::placeholder {
      font-size: 16px;
      padding: 2px;
    }
  }
  .sc-jSMfEi {
    min-height: 38px !important;
    border-radius: 0.25rem !important;
    overflow: hidden;
    &:focus {
      box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
    }
    &:focus-within {
      box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
    }
  }
  .eoOphk {
    &:focus {
      background: ${colors.blue};
    }
  }
  .fdjKYC {
    min-height: 38px !important;
    border-radius: 0.25rem !important;
    overflow: hidden;
    &:focus {
      box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
    }
  }
  .kvwrSX {
    min-height: 38px !important;
    border-radius: 0.25rem !important;
    overflow: hidden;
    box-shadow: 0 0 0 0.25rem ${$secondaryWithAlpha('0.15')} !important;
  }
  label {
    border-radius: 4px !important;
    border: 1px solid ${colors.grayBorder} !important;
    & + div {
      z-index: 99999;
    }
  }
  .covbmQ {
    top: 13px !important;
  }
`

const InputError = styled.span`
  font-size: 90%;
  color: ${colors.dangerLight};
`

const sizeClasses = {
  small: 'text-sm h-10',
  medium: 'h-12',
  big: 'h-14',
}

const ThemeWrapper: React.FC<{ theme: CustomTheme; children: React.ReactNode }> = ({ theme, children }) => {
  return <ThemeProvider theme={theme as any}>{children}</ThemeProvider>
}

export const PixelInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      _className,
      _label,
      name,
      error,
      _children,
      variant = 'normal',
      dimension = 'big',
      shadow = false,
      disabled = false,
      _isTextarea = false,
      as = 'text',
      type = 'text',
      onChange,
      value,
      inputClassName,
      isMakingInput = false,
      parentStyle = {},
      _startDate,
      _endDate,
      labelKey,
      noPadding = false,
      invalid = false,
      showsearchicon = 1,
      placeholder = '',
      isShowLabel = true,
      customLabel = '',
      isClearOnSelection = false,
      onSelectedOption = () => {
        // Empty function for default prop
      },
      showResetDate = false,
      showClose = false,
      ...rest
    },
    ref,
  ) => {
    const [showDatePicker, setShowDatePicker] = useState<'startDate' | 'endDate' | null>(
      as === 'dateRange' ? null : null,
    )
    const [showLabel, setShowLabel] = React.useState<boolean>(false)
    const [focus, setFocus] = React.useState<boolean>(false)

    React.useEffect(() => {
      if (value || customLabel !== '') {
        setShowLabel(true)
      } else if (value === '' || value === null || value === undefined) {
        setShowLabel(false)
      }
    }, [value, customLabel])

    const truncate = (label: string, max: number): string => {
      if (label.length > max) {
        return label.substring(0, max) + '...'
      }
      return label
    }

    if (as === 'textarea') {
      (rest as any)['as'] = as
    }

    const customTheme: CustomTheme = {
      breakpoints: ['40em', '52em', '64em'],
      reactDatepicker: {
        dateRangeZIndex: 1,
      },
      splash: {
        bg: colors.white,
        gradient: 'linear-gradient(180deg, #fff 70%, transparent 30%)',
        logoFill: colors.black,
        progressBg: '#f0f0f0',
        progressAfterBg: colors.chartCyan,
        titleColor: colors.black,
        subTitleColor: colors.grayText,
        iconColor: colors.chartCyan,
      },
      layout: {
        bg: colors.white,
        contentBoxShadowColor: 'rgba(0, 0, 0, 0.1)',
      },
      search: {
        iconColor: colors.grayText,
      },
      sidebar: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      options: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      unselectedChat: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      chatRoom: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      common: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      alert: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      badge: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      sentMessage: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      receivedMessage: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
      encryptionMessage: {
        bg: colors.white,
        boxShadow: 'rgba(0, 0, 0, 0.1)',
      },
    }

    return (
      <PixelInputContainer
        name={name}
        invalid={invalid}
        className="overFlowCustom"
        style={parentStyle}
        onFocus={() => {
          setFocus(true)
        }}
        onBlur={() => {
          setFocus(false)
        }}
      >
        {isShowLabel && (
          <StyledLabel
            focus={focus ? 'true' : 'false'}
            className={showLabel ? 'showLabell' : 'testing'}
            showsearchicon={showsearchicon}
          >
            {truncate(customLabel !== '' ? customLabel : placeholder, 25)}
          </StyledLabel>
        )}

        {isMakingInput ? (
          <Inputmask
            value={value}
            onChange={onChange}
            disabled={disabled}
            rest={rest}
            id={name}
            name={name}
            type={type}
            ref={ref}
            className={cn(
              'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
              shadow && 'focus:shadow',
              variantClasses[variant],
              sizeClasses[dimension],
              disabled && 'bg-gray-100 cursor-not-allowed',
              inputClassName,
            )}
          />
        ) : (
          <React.Fragment>
            {as === 'datePicker' ? (
              <React.Fragment>
                <Datepicker
                  ref={ref}
                  value={value}
                  className={cn(
                    'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                    shadow && 'focus:shadow',
                    variantClasses[variant],
                    sizeClasses[dimension],
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    inputClassName,
                  )}
                  disabled={disabled}
                  aria-invalid={error ? 'true' : 'false'}
                  rest={rest}
                  showResetDate={showResetDate}
                  showClose={showClose}
                  label={_label}
                  placeholder={placeholder}
                  inputId={name}
                  onChange={onChange}
                  onDateChange={(data: { date: string }) => {
                    if (onChange) {
                      onChange({
                        target: { value: data.date },
                      } as ChangeEvent<HTMLInputElement>)
                    }
                    setShowDatePicker(null)
                  }}
                  onFocusChange={(focusedInput: boolean) => setShowDatePicker(focusedInput ? 'startDate' : null)}
                  showDatepicker={showDatePicker !== null}
                />
              </React.Fragment>
            ) : as === 'dateRange' ? (
              <React.Fragment>
                <StyledDateRangePicker
                  className={cn(
                    'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                    shadow && 'focus:shadow',
                    variantClasses[variant],
                    sizeClasses[dimension],
                    disabled && 'bg-gray-100 cursor-not-allowed',
                    inputClassName,
                  )}
                  style={{ display: 'none' }}
                  disabled={disabled}
                  aria-invalid={error ? 'true' : 'false'}
                  {...rest}
                  showResetDates={showResetDate}
                  showSelectedDates={false}
                  showClose={showClose}
                  exactMinBookingDays={false}
                  rtl={false}
                  vertical={false}
                  startDateInputId={name}
                  endDateInputId={name}
                  onDatesChange={(data: OnDatesChangeProps) => {
                    if (onChange) {
                      const event = {
                        target: {
                          name,
                          value: {
                            startDate: data.startDate,
                            endDate: data.endDate,
                          },
                        },
                      } as any as ChangeEvent<HTMLInputElement>
                      onChange(event)
                    }
                    setShowDatePicker(data.focusedInput)
                  }}
                  onFocusChange={(focusedInput: 'startDate' | 'endDate' | null) => 
                    setShowDatePicker(focusedInput)
                  }
                  focusedInput={showDatePicker}
                  endDate={(value as DateRangeData)?.endDate}
                  startDate={(value as DateRangeData)?.startDate}
                />
              </React.Fragment>
            ) : as === 'typeahead' ? (
              <TypeAHead
                onSelectedOption={onSelectedOption}
                isClearOnSelection={isClearOnSelection}
                customLabel={customLabel}
                isShowLabel={isShowLabel}
                placeholder={placeholder}
                id={name}
                name={name}
                type={type}
                ref={ref}
                labelKey={labelKey}
                className={cn(
                  'flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                  shadow && 'focus:shadow',
                  variantClasses[variant],
                  sizeClasses[dimension],
                  disabled && 'bg-gray-100 cursor-not-allowed',
                  inputClassName,
                )}
                disabled={disabled}
                value={value}
                spellCheck="false"
                onChange={onChange}
                invalid={invalid}
                aria-invalid={error ? 'true' : 'false'}
                {...rest}
              />
            ) : (
              <Input
                showsearchicon={showsearchicon}
                placeholder={placeholder}
                id={name}
                name={name}
                type={type}
                ref={ref}
                className={
                  noPadding
                    ? cn(
                        'px-1 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                        shadow && 'focus:shadow',
                        variantClasses[variant],
                        sizeClasses[dimension],
                        disabled && 'bg-gray-100 cursor-not-allowed',
                        inputClassName,
                      )
                    : cn(
                        'px-4 flex items-center w-full appearance-none transition duration-300 ease-in-out text-heading text-sm focus:outline-none focus:ring-0',
                        shadow && 'focus:shadow',
                        variantClasses[variant],
                        sizeClasses[dimension],
                        disabled && 'bg-gray-100 cursor-not-allowed',
                        inputClassName,
                      )
                }
                disabled={disabled}
                value={value}
                spellCheck="false"
                onChange={onChange}
                aria-invalid={error ? 'true' : 'false'}
                rest={rest}
              />
            )}
          </React.Fragment>
        )}

        {error && <InputError className="my-2 text-xs text-red-500">{error}</InputError>}
      </PixelInputContainer>
    )
  },
)

const StyledDateRangePicker = styled(DateRangeInput)`
  .sc-dkzDqf {
    z-index: 99999;
  }
`

export default PixelInput
